import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BankFootageItem } from 'fym-shared/src/music24/musicVideoApi';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoEditorMode } from '../MusicVideoEditorContext';
import { useLazyGetFootageBankQuery, useLazyGetMusicVideoShowcaseLookupQuery } from '../musicVideoEditorApi';
import { bankUuidChunks, bankUuidsOfDoc, missingBankUuids } from './bankLookup';

export interface BankCatalogOptions {
  /** `showcase` reads `GET /public/music-video-showcase/bank/lookup` in place of the account route. */
  mode?: MusicVideoEditorMode;
  /** Clips that are known already (the showcase answer holds the clips of its cut): no read for them. */
  seed?: readonly BankFootageItem[];
}

function seedMap(seed: readonly BankFootageItem[] | undefined): ReadonlyMap<string, BankFootageItem> {
  return new Map((seed ?? []).map((item) => [item.uuid, item]));
}

/**
 * The bank clips that the cut uses. The cut names its bank clips by uuid only, and the preview and
 * the timeline need their pictures and video links: this hook reads them with
 * `GET /footage/bank?uuids=` (at most `BANK_FOOTAGE_UUIDS_MAX` a request). A clip that comes into the
 * cut later (a drop from the Bank tab, a recut) is read when it appears. A request that fails is
 * asked again at the next change of the cut; the clips read before it stay.
 *
 * The video links of the answer end after one hour. `refresh` reads every clip of the cut again from
 * the server (no cache); the clips read before it stay until the new answer replaces them.
 *
 * In showcase mode the same reads go to the public lookup of the showcase, and the clips of the
 * showcase answer start the catalog (`seed`).
 */
export function useBankCatalog(
  accountId: number,
  doc: Pick<MusicVideoDoc, 'clips'>,
  { mode = 'account', seed }: BankCatalogOptions = {}
): { items: BankFootageItem[]; isDone: boolean; refresh: () => void } {
  const [loadAccountClips] = useLazyGetFootageBankQuery();
  const [loadShowcaseClips] = useLazyGetMusicVideoShowcaseLookupQuery();
  const [byUuid, setByUuid] = useState<ReadonlyMap<string, BankFootageItem>>(() => seedMap(seed));
  const [pending, setPending] = useState(0);
  const [refreshRound, setRefreshRound] = useState(0);
  const doneRound = useRef(0);
  const requested = useRef(new Set<string>());
  const loadedAccount = useRef(accountId);
  const uuids = useMemo(() => bankUuidsOfDoc(doc), [doc]);
  const isShowcase = mode === 'showcase';

  // Another account knows other clips: start again (the first render keeps the seed).
  useEffect(() => {
    if (loadedAccount.current === accountId) {
      return;
    }
    loadedAccount.current = accountId;
    requested.current = new Set();
    setByUuid(new Map());
  }, [accountId]);

  useEffect(() => {
    const isRefresh = refreshRound !== doneRound.current;
    doneRound.current = refreshRound;
    // A refresh asks for every clip of the cut again, past the cache; else only the new clips.
    const missing = isRefresh ? uuids : missingBankUuids(uuids, new Set(byUuid.keys()), requested.current);
    if (missing.length === 0) {
      return;
    }
    missing.forEach((uuid) => requested.current.add(uuid));
    bankUuidChunks(missing).forEach((chunk) => {
      setPending((count) => count + 1);
      const request = isShowcase
        ? loadShowcaseClips({ uuids: chunk }, !isRefresh)
        : loadAccountClips({ accountId, uuids: chunk }, !isRefresh);
      request
        .unwrap()
        .then((answer) => {
          setByUuid((before) => {
            const next = new Map(before);
            answer.items.forEach((item) => next.set(item.uuid, item));
            return next;
          });
        })
        .catch((error: unknown) => {
          chunk.forEach((uuid) => requested.current.delete(uuid));
          console.error('Music video editor: the bank clips of the cut did not load', error);
        })
        .finally(() => setPending((count) => count - 1));
    });
  }, [accountId, uuids, byUuid, isShowcase, loadAccountClips, loadShowcaseClips, refreshRound]);

  const refresh = useCallback(() => setRefreshRound((round) => round + 1), []);
  const items = useMemo(() => [...byUuid.values()], [byUuid]);
  return { items, isDone: pending === 0, refresh };
}
